import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { CourierUserApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../../shared/api-config';
import { getCourier } from '../../state/courierSlice';
import { login } from '../../state/authSlice';
import { showErrorToast } from '../../utils/toast';
import { extractApiError } from '../../utils/apiError';

const GENERIC_PASSKEY_ERROR =
  'We could not sign you in with that passkey. Please try again.';

/**
 * Errors the browser raises when nothing actually went wrong — the user closed
 * the sheet, or the ceremony was superseded. Shouting about these would make a
 * deliberate cancel look like a failure.
 */
const SILENT_ERROR_NAMES = ['NotAllowedError', 'AbortError'];

/**
 * Passkey (WebAuthn) sign-in.
 *
 * Two ways in, one button: with no email typed the server omits
 * allowCredentials and the browser shows its own account picker (a
 * discoverable credential); with an email it narrows the ceremony to that
 * account.
 *
 * Like the other sign-in hooks here, this does not navigate — dispatching
 * `login` flips `isAuthenticated` and the router swaps the /auth route for
 * AuthRedirect.
 */
export const usePasskeyAuth = () => {
  const dispatch = useDispatch();
  const courier = useSelector(getCourier);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const isSupported = browserSupportsWebAuthn();

  const fail = (message: string) => {
    setError(message);
    showErrorToast({ title: 'Passkey Sign-In Failed', message });
    return { success: false };
  };

  const signInWithPasskey = async (email?: string) => {
    setError(null);

    if (!isSupported) {
      // Nothing to fail loudly about: the button is hidden in this case, so
      // reaching here means a programmatic call.
      return { success: false };
    }

    if (!courier?.realm) {
      return fail('Courier configuration not found');
    }

    setIsLoading(true);

    try {
      const authApi = new CourierUserApi(apiConfig());

      const { options } = await authApi.courierUserPasskeyLoginOptions({
        realm: courier.realm,
        ...(email ? { email } : {}),
      });

      const credential = await startAuthentication(options as never);

      const response = await authApi.courierUserPasskeyLoginVerify({
        realm: courier.realm,
        credential: credential as never,
      });

      if (!response?.id) {
        return fail(GENERIC_PASSKEY_ERROR);
      }

      dispatch(
        login({
          id: response.id,
          ttl: response.ttl,
          created: response.created,
          userId: response.userId,
          user: response.user,
        } as never)
      );

      return { success: true };
    } catch (err) {
      if (SILENT_ERROR_NAMES.includes((err as Error)?.name)) {
        return { success: false };
      }

      const { message } = await extractApiError(err, GENERIC_PASSKEY_ERROR);
      return fail(message);
    } finally {
      setIsLoading(false);
    }
  };

  return { signInWithPasskey, isSupported, isLoading, error, clearError };
};
